import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  Timestamp,
  onSnapshot,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from './firebase';

export const NOTIFICATION_TYPES = {
  PACKAGE_CREATED: 'package_created',
  PACKAGE_UPDATED: 'package_updated',
  SUBSCRIPTION_REMINDER: 'subscription_reminder',
  NEW_JOB: 'new_job',
  NEW_APPLICATION: 'new_application',
  APPLICATION_STATUS_UPDATE: 'application_status_update'
};

export const NOTIFICATION_STATUS = {
  UNREAD: 'unread',
  READ: 'read'
};

// Create a notification
export const createNotification = async (notificationData) => {
  try {
    const notification = {
      ...notificationData,
      status: NOTIFICATION_STATUS.UNREAD,
      createdAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'notifications'), notification);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: error.message };
  }
};

// Create package notification for all users of specific types
export const createPackageNotificationForUsers = async (packageData, userTypes = ['jobseeker', 'employer']) => {
  try {
    // Get all users of specified types
    const usersQuery = query(
      collection(db, 'users'),
      where('userType', 'in', userTypes)
    );
    
    const usersSnapshot = await getDocs(usersQuery);
    const notifications = [];

    // Create notification for each user
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const userId = userDoc.id;

      const notificationData = {
        userId: userId,
        type: NOTIFICATION_TYPES.PACKAGE_CREATED,
        title: `New ${packageData.type === 'job_seeker' ? 'Job Seeker' : 'Employer'} Package Available!`,
        message: `A new package "${packageData.name}" has been created with exciting features. Check it out now!`,
        data: {
          packageId: packageData.id,
          packageName: packageData.name,
          packageType: packageData.type,
          packagePrice: packageData.price,
          packageDuration: packageData.duration
        },
        targetUserType: userData.userType
      };

      const result = await createNotification(notificationData);
      if (result.success) {
        notifications.push(result.id);
      }
    }

    return { 
      success: true, 
      notificationsCreated: notifications.length,
      notificationIds: notifications 
    };
  } catch (error) {
    console.error('Error creating package notifications:', error);
    return { success: false, error: error.message };
  }
};

// Get notifications for a specific user
export const getUserNotifications = async (userId, limitCount = 20) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const notifications = [];

    querySnapshot.forEach((doc) => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort in memory by createdAt (newest first)
    notifications.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    // Apply limit after sorting
    const limitedNotifications = notifications.slice(0, limitCount);

    return { success: true, notifications: limitedNotifications };
  } catch (error) {
    console.error('Error fetching user notifications:', error);
    return { success: false, error: error.message };
  }
};

// Get unread notifications count for a user
export const getUnreadNotificationsCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('status', '==', NOTIFICATION_STATUS.UNREAD)
    );

    const querySnapshot = await getDocs(q);
    return { success: true, count: querySnapshot.size };
  } catch (error) {
    console.error('Error fetching unread notifications count:', error);
    return { success: false, error: error.message };
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      status: NOTIFICATION_STATUS.READ,
      readAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { success: false, error: error.message };
  }
};

// Mark all notifications as read for a user
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('status', '==', NOTIFICATION_STATUS.UNREAD)
    );

    const querySnapshot = await getDocs(q);
    const updatePromises = [];

    querySnapshot.forEach((doc) => {
      const notificationRef = doc.ref;
      updatePromises.push(
        updateDoc(notificationRef, {
          status: NOTIFICATION_STATUS.READ,
          readAt: Timestamp.now()
        })
      );
    });

    await Promise.all(updatePromises);
    return { success: true, updatedCount: updatePromises.length };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return { success: false, error: error.message };
  }
};

// Delete a notification
export const deleteNotification = async (notificationId) => {
  try {
    await deleteDoc(doc(db, 'notifications', notificationId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return { success: false, error: error.message };
  }
};

// Delete all notifications for a user
export const deleteAllUserNotifications = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const deletePromises = [];

    querySnapshot.forEach((doc) => {
      deletePromises.push(deleteDoc(doc.ref));
    });

    await Promise.all(deletePromises);
    return { success: true, deletedCount: deletePromises.length };
  } catch (error) {
    console.error('Error deleting all user notifications:', error);
    return { success: false, error: error.message };
  }
};

// Real-time listener for user notifications
export const subscribeToUserNotifications = (userId, callback) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const notifications = [];
      querySnapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data()
        });
      });
      callback(notifications);
    }, (error) => {
      console.error('Error in notification listener:', error);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error subscribing to notifications:', error);
    return null;
  }
};
